/*
 * Datas comemorativas: em cada período o sistema ganha um tema (cores do topo, uma
 * saudação na tela inicial e confetes temáticos). Só visual: PDFs e relatórios não mudam.
 *
 * Aparece quando: hoje está no período de alguma data (horário de Brasília), o módulo
 * "datas_comemorativas" está liberado para o perfil (o admin desliga em Módulos por
 * perfil) e a pessoa não desligou em Configurações › Datas comemorativas.
 */
import { hojeBrasilia } from "@/utils/dataBrasilia";

export const MODULO = "datas_comemorativas";

/** Domingo de Páscoa (algoritmo de Meeus/Jones/Butcher), como AAAA-MM-DD. */
export function pascoa(ano) {
  const a = ano % 19;
  const b = Math.floor(ano / 100);
  const c = ano % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mes = Math.floor((h + l - 7 * m + 114) / 31);
  const dia = ((h + l - 7 * m + 114) % 31) + 1;
  return `${ano}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
}

function somarDias(data, dias) {
  const [ano, mes, dia] = data.split("-").map(Number);
  const d = new Date(Date.UTC(ano, mes - 1, dia + dias));
  return d.toISOString().slice(0, 10);
}

/**
 * Catálogo. periodo(ano) devolve [início, fim] (AAAA-MM-DD, inclusive).
 * confete: celebração da data (src/plugins/confete.js). estilo "chuva" cai do topo,
 * "fogos" estoura pela tela e "canhoes" sai dos dois lados; emojis viram os confetes.
 */
export const DATAS = [
  {
    chave: "carnaval",
    nome: "Carnaval",
    icone: "mdi-drama-masks",
    periodo: (ano) => [somarDias(pascoa(ano), -50), somarDias(pascoa(ano), -46)],
    titulo: "Bom Carnaval!",
    texto: "Confete, serpentina e ponto batido: folia com responsabilidade.",
    frase: "🎭 Bom Carnaval!",
    gradiente: "linear-gradient(90deg, #4f008f 0%, #c026d3 50%, #f59e0b 100%)",
    confete: {
      estilo: "canhoes",
      emojis: ["🎭", "🎉", "🎊"],
      cores: ["#f59e0b", "#22c55e", "#ec4899", "#38bdf8", "#a855f7"],
    },
  },
  {
    chave: "pascoa",
    nome: "Páscoa",
    icone: "mdi-rabbit",
    periodo: (ano) => [somarDias(pascoa(ano), -7), pascoa(ano)],
    titulo: "Feliz Páscoa!",
    texto: "Que a semana seja doce como chocolate.",
    frase: "🐰 Feliz Páscoa!",
    gradiente: "linear-gradient(90deg, #4f008f 0%, #7c3aed 55%, #f9a8d4 100%)",
    confete: {
      estilo: "chuva",
      emojis: ["🐰", "🥚", "🍫"],
      cores: ["#f9a8d4", "#a5f3fc", "#fde68a", "#c4b5fd"],
    },
  },
  {
    chave: "festa_junina",
    nome: "Festa Junina",
    icone: "mdi-campfire",
    periodo: (ano) => [`${ano}-06-01`, `${ano}-06-30`],
    titulo: "Arraiá do ponto!",
    texto: "Olha a cobra! É mentira... o ponto tá em dia.",
    frase: "🔥 Bom São João!",
    gradiente: "linear-gradient(90deg, #4f008f 0%, #b45309 55%, #f59e0b 100%)",
    confete: {
      estilo: "canhoes",
      emojis: ["🌽", "🔥", "🎉"],
      cores: ["#ef4444", "#22c55e", "#3b82f6", "#f59e0b"],
    },
  },
  {
    chave: "halloween",
    nome: "Halloween",
    icone: "mdi-halloween",
    periodo: (ano) => [`${ano}-10-24`, `${ano}-11-01`],
    titulo: "Feliz Halloween!",
    texto: "Doces ou travessuras? Por aqui, só pontos em dia.",
    frase: "🎃 Sem sustos hoje!",
    gradiente: "linear-gradient(90deg, #2a0a45 0%, #4f008f 55%, #c2410c 100%)",
    confete: {
      estilo: "chuva",
      emojis: ["🎃", "🦇", "👻", "🍬"],
      cores: ["#ff9a00", "#ff7400", "#ff4d00"],
    },
  },
  {
    chave: "natal",
    nome: "Natal",
    icone: "mdi-pine-tree",
    periodo: (ano) => [`${ano}-12-01`, `${ano}-12-25`],
    titulo: "Feliz Natal!",
    texto: "Que o fim de ano traga descanso e boas memórias.",
    frase: "🎄 Boas festas!",
    gradiente: "linear-gradient(90deg, #4f008f 0%, #991b1b 55%, #15803d 100%)",
    confete: {
      estilo: "chuva",
      emojis: ["🎄", "⭐", "❄️", "🎁"],
      cores: ["#8d960f", "#be0f10", "#445404"],
    },
  },
  {
    chave: "ano_novo",
    nome: "Ano Novo",
    icone: "mdi-firework",
    // Começa no fim de um ano e termina no seguinte.
    periodo: (ano) => [`${ano}-12-26`, `${ano + 1}-01-02`],
    titulo: "Feliz Ano Novo!",
    texto: "Que o novo ano venha cheio de conquistas.",
    frase: "🎆 Feliz Ano Novo!",
    gradiente: "linear-gradient(90deg, #1e1b4b 0%, #4f008f 55%, #ca8a04 100%)",
    confete: {
      estilo: "fogos",
      emojis: ["🎆", "🥂", "✨"],
      cores: ["#facc15", "#fde68a", "#ffffff"],
    },
  },
];

/** Datas com o período calculado para o ano informado (para listar na tela). */
export function datasDoAno(ano) {
  return DATAS.map((data) => {
    const [inicio, fim] = data.periodo(ano);
    return { ...data, inicio, fim };
  });
}

/** Data comemorativa em andamento hoje, ou null. */
export function dataComemorativaDeHoje(hoje = hojeBrasilia()) {
  const ano = Number(hoje.slice(0, 4));
  // Ano anterior também entra: o Ano Novo de dezembro termina em janeiro.
  const candidatas = [...datasDoAno(ano - 1), ...datasDoAno(ano)];
  return candidatas.find((data) => hoje >= data.inicio && hoje <= data.fim) || null;
}

/** Preferência da conta: vem ligada; só false desliga. */
export function preferenciaLigada(user) {
  return !user || user.datas_comemorativas !== false;
}

/**
 * Tema que deve aparecer agora para o usuário, ou null.
 * moduloAtivo é o getter AppConfigurations/moduloAtivo.
 */
export function dataComemorativaAtiva(user, moduloAtivo) {
  if (!preferenciaLigada(user)) return null;
  if (moduloAtivo && !moduloAtivo(MODULO)) return null;
  return dataComemorativaDeHoje();
}

/** Classe e gradiente do topo no <html>, usados pelo CSS global (src/app.css). */
export function aplicarNoDocumento(data) {
  const html = document.documentElement;
  [...html.classList]
    .filter((classe) => classe.startsWith("data-comemorativa"))
    .forEach((classe) => html.classList.remove(classe));

  if (data) {
    html.classList.add("data-comemorativa", `data-comemorativa--${data.chave}`);
    html.style.setProperty("--data-comemorativa-gradiente", data.gradiente);
  } else {
    html.style.removeProperty("--data-comemorativa-gradiente");
  }
}
